import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  Font
} from '@react-pdf/renderer';

Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 700 }]
});

// ─── Design tokens ──────────────────────────────────────────────
const C = {
  primary:     '#1565C0',
  primaryDark: '#0D47A1',
  primaryLight:'#E3F2FD',
  sectionBg:   '#1976D2',
  subtotalBg:  '#BBDEFB',
  altRow:      '#F5F8FF',
  white:       '#FFFFFF',
  dark:        '#1A1A2E',
  border:      '#B0BEC5',
  green:       '#1B5E20',
  red:         '#B71C1C',
  orange:      '#E65100',
  gray:        '#546E7A',
  lightGray:   '#ECEFF1',
};

// ─── Column definitions (must sum to 100%) ───────────────────────
const COL = {
  sr:        '3%',
  date:      '6%',
  type:      '7%',
  patient:   '14%',
  uhid:      '6%',
  billNo:    '6%',
  payMode:   '6%',
  consultant:'10%',
  services:  '10%',
  panel:     '7%',
  credit:    '5%',
  recd1:     '6%',
  recd2:     '6%',
  total:     '8%',
};

const styles = StyleSheet.create({
  page: { padding: '16pt 18pt', fontSize: 7, color: C.dark, backgroundColor: C.white },

  // ── Header ──────────────────────────────────────────
  logo:        { width: '100%', height: 64, marginBottom: 2 },
  reportTitle: { fontSize: 11, fontFamily: 'Roboto', fontWeight: 700, textAlign: 'center', color: C.primaryDark, marginBottom: 1, letterSpacing: 1 },
  reportSub:   { fontSize: 7.5, textAlign: 'center', color: C.gray, marginBottom: 6 },
  headerRule:  { height: 2, backgroundColor: C.primary, marginBottom: 8 },

  // ── Table header ────────────────────────────────────
  tableHead: { flexDirection: 'row', backgroundColor: C.sectionBg, borderRadius: 2 },
  th: {
    color: C.white, fontFamily: 'Roboto', fontWeight: 700, fontSize: 6.5,
    paddingVertical: 4, paddingHorizontal: 2,
    textAlign: 'center', borderRightWidth: 0.5, borderRightColor: '#90CAF9',
  },

  // ── Section group header ─────────────────────────────
  sectionHeader: {
    flexDirection: 'row', backgroundColor: C.primaryLight,
    borderLeftWidth: 3, borderLeftColor: C.primary,
    paddingVertical: 3, paddingHorizontal: 6, marginTop: 7, marginBottom: 0,
  },
  sectionTitle: { fontSize: 7.5, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark },
  sectionCount: { fontSize: 6.5, color: C.gray, marginLeft: 4 },

  // ── Data rows ────────────────────────────────────────
  row:     { flexDirection: 'row', borderBottomWidth: 0.3, borderBottomColor: C.border },
  rowEven: { backgroundColor: C.white },
  rowOdd:  { backgroundColor: C.altRow },
  td: {
    fontSize: 6.5, paddingVertical: 2.5, paddingHorizontal: 2,
    borderRightWidth: 0.3, borderRightColor: C.border,
  },
  tdRight: { textAlign: 'right' },
  tdLeft:  { textAlign: 'left' },
  tdCenter:{ textAlign: 'center' },

  // ── Section subtotal row ─────────────────────────────
  subtotalRow: {
    flexDirection: 'row', backgroundColor: C.subtotalBg,
    borderTopWidth: 0.5, borderTopColor: C.primary,
    borderBottomWidth: 0.5, borderBottomColor: C.primary,
    paddingVertical: 3,
  },
  subtotalLabel: { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right', paddingHorizontal: 4 },
  subtotalAmt:   { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right', paddingHorizontal: 4 },

  // ── Refund subtotal ──────────────────────────────────
  refundSubtotal: {
    flexDirection: 'row', backgroundColor: '#FFEBEE',
    borderTopWidth: 0.5, borderTopColor: C.red,
    paddingVertical: 3,
  },

  // ── Summary box ──────────────────────────────────────
  summaryWrap: { marginTop: 14, flexDirection: 'row' },

  // ── Summary boxes ────────────────────────────────────
  summaryBox: {
    flex: 1, borderWidth: 1, borderColor: C.primary, borderRadius: 4, marginRight: 8,
  },
  summaryBoxTitle: {
    backgroundColor: C.primary, color: C.white, fontFamily: 'Roboto', fontWeight: 700,
    fontSize: 7, textAlign: 'center', paddingVertical: 4,
  },
  modeRow: {
    flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: C.border,
    paddingVertical: 3, paddingHorizontal: 6, alignItems: 'center',
  },
  modeRowLast: {
    flexDirection: 'row', paddingVertical: 3, paddingHorizontal: 6, alignItems: 'center',
  },
  modeBadge: {
    fontSize: 5.5, paddingHorizontal: 3, paddingVertical: 1, borderRadius: 2,
    marginRight: 3, textAlign: 'center',
  },
  modeLabel: { flex: 1, fontSize: 7, color: C.dark },
  modeSub:   { fontSize: 5.5, color: C.gray },
  modeAmt:   { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right', minWidth: 65 },
  modeAmtRed:{ fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.red, textAlign: 'right', minWidth: 65 },
  modeAmtGreen: { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.green, textAlign: 'right', minWidth: 65 },
  modeDivider: { height: 1, backgroundColor: C.primary, marginVertical: 2 },

  // Section totals box
  sectionBox: {
    flex: 1.1, borderWidth: 1, borderColor: C.primary, borderRadius: 4,
  },
  sBoxTitle: {
    backgroundColor: C.primary, color: C.white, fontFamily: 'Roboto', fontWeight: 700,
    fontSize: 7, textAlign: 'center', paddingVertical: 4,
  },
  sBoxRow: {
    flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: C.border,
    paddingVertical: 3, paddingHorizontal: 6,
  },
  sBoxRowLast: {
    flexDirection: 'row', paddingVertical: 3, paddingHorizontal: 6,
  },
  sBoxLabel: { flex: 1, fontSize: 7, color: C.dark },
  sBoxAmt:   { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right', minWidth: 55 },
  sBoxAmtRed:{ fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.red, textAlign: 'right', minWidth: 55 },

  // Grand total box
  grandBox: {
    marginTop: 10, borderWidth: 1.5, borderColor: C.primaryDark, borderRadius: 4,
    backgroundColor: C.primaryDark,
  },
  grandRow: {
    flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 10,
    alignItems: 'center',
  },
  grandLabel: { flex: 1, fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, color: C.white },
  grandAmt:   { fontSize: 11, fontFamily: 'Roboto', fontWeight: 700, color: '#FFD54F' },

  // Credit outstanding box
  creditBox: {
    marginTop: 6, borderWidth: 1, borderColor: '#FF8F00', borderRadius: 4,
    backgroundColor: '#FFF8E1',
    flexDirection: 'row', paddingVertical: 4, paddingHorizontal: 10, alignItems: 'center',
  },
  creditLabel: { flex: 1, fontSize: 7.5, color: '#E65100' },
  creditAmt:   { fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: '#E65100' },

  // Footer
  footer: {
    position: 'absolute', bottom: 10, left: 18, right: 18,
    flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: C.border,
    paddingTop: 4,
  },
  footerLeft:  { flex: 1, fontSize: 6, color: C.gray },
  footerRight: { fontSize: 6, color: C.gray, textAlign: 'right' },

  // Empty state
  emptyRow: { paddingVertical: 5, paddingHorizontal: 8 },
  emptyText: { fontSize: 6.5, color: C.gray, fontStyle: 'italic' },
});

// ─── Helpers ─────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt)) return '-';
  return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
}

// Entry time: use the stored `time` field (String); fall back to createdAt for old records
function rowTime(row) {
  if (row?.time) return String(row.time);
  if (!row?.createdAt) return '';
  const dt = new Date(row.createdAt);
  if (isNaN(dt)) return '';
  return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}

function fmt(n) {
  const num = Number(n) || 0;
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtINR(n) { return `₹ ${fmt(n)}`; }

function nowStr() {
  const d = new Date();
  return `${fmtDate(d)}  ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

// ─── Sub-components ──────────────────────────────────────────────
const TH = ({ w, children, align = 'center' }) => (
  <PDFText style={[styles.th, { width: w, textAlign: align }]}>{children}</PDFText>
);

const TD = ({ w, children, align = 'center', bold, color }) => (
  <PDFText style={[
    styles.td,
    { width: w, textAlign: align, color: color || C.dark },
    bold ? { fontFamily: 'Roboto', fontWeight: 700 } : {}
  ]}>
    {children ?? ''}
  </PDFText>
);

const TableHeader = () => (
  <PDFView style={styles.tableHead}>
    <TH w={COL.sr}>#</TH>
    <TH w={COL.date} align="left">Date / Time</TH>
    <TH w={COL.type} align="left">Type</TH>
    <TH w={COL.patient} align="left">Patient Name</TH>
    <TH w={COL.uhid}>UHID</TH>
    <TH w={COL.billNo}>Bill/Adm No.</TH>
    <TH w={COL.payMode}>Pay Mode</TH>
    <TH w={COL.consultant} align="left">Consultant</TH>
    <TH w={COL.services} align="left">Services</TH>
    <TH w={COL.panel} align="left">Panel</TH>
    <TH w={COL.credit} align="right">Credit</TH>
    <TH w={COL.recd1} align="right">Recd 1</TH>
    <TH w={COL.recd2} align="right">Recd 2</TH>
    <TH w={COL.total} align="right">Recd Amt.</TH>
  </PDFView>
);

const SectionHeader = ({ title, count }) => (
  <PDFView style={styles.sectionHeader}>
    <PDFText style={styles.sectionTitle}>{title}</PDFText>
    {count !== undefined && (
      <PDFText style={styles.sectionCount}>({count} record{count !== 1 ? 's' : ''})</PDFText>
    )}
  </PDFView>
);

const SubtotalRow = ({ label, amount, isRefund }) => (
  <PDFView style={isRefund ? styles.refundSubtotal : styles.subtotalRow}>
    <PDFText style={[styles.subtotalLabel, { flex: 1, color: isRefund ? C.red : C.primaryDark }]}>
      {label}:
    </PDFText>
    <PDFText style={[styles.subtotalAmt, { width: COL.total, color: isRefund ? C.red : C.primaryDark }]}>
      {fmtINR(amount)}
    </PDFText>
  </PDFView>
);

const EmptySection = () => (
  <PDFView style={styles.emptyRow}>
    <PDFText style={styles.emptyText}>No records found for this period.</PDFText>
  </PDFView>
);

// ─── Main Component ───────────────────────────────────────────────
export const CollectionSummaryReport = ({ data }) => {

  // ── Per-section stats ──
  const opd        = data?.opdData?.stats            || {};
  const opdBill    = data?.opdBillingData?.stats      || {};
  const receipt    = data?.receiptVoucherData?.stats  || {};
  const refund     = data?.paymentRefundData?.stats   || {};
  const ipd        = data?.indoorServiceData?.stats   || {};
  const lab        = data?.labInvestigationData?.stats|| {};

  // ── Billing-only payment-mode totals (OPD + OPD Bill + IPD + Lab — NOT receipt vouchers)
  // Receipt vouchers are tracked separately so they are not double-counted in the grand total
  const billingCash  = (opd.recd_amt_cash   || 0) + (opdBill.recd_amt_cash  || 0)
                     + (ipd.recd_amt_cash    || 0) + (lab.recd_amt_cash      || 0);
  const billingBank  = (opd.recd_amt_bank   || 0) + (opdBill.recd_amt_bank  || 0)
                     + (ipd.recd_amt_bank    || 0) + (lab.recd_amt_bank      || 0);
  const billingBoth  = (opd.recd_amt_both   || 0) + (opdBill.recd_amt_both  || 0)
                     + (ipd.recd_amt_both    || 0) + (lab.recd_amt_both      || 0);
  const totalCredit  = (opd.recd_amt_credit || 0) + (opdBill.recd_amt_credit|| 0)
                     + (ipd.recd_amt_credit  || 0) + (lab.recd_amt_credit    || 0);

  // ── Receipt Voucher breakdown by mode ──
  const receiptCash  = receipt.recd_amt_cash  || 0;
  const receiptBank  = receipt.recd_amt_bank  || 0;
  const receiptBoth  = receipt.recd_amt_both  || 0;
  const receiptTotal = receipt.recd_amt       || 0;

  // ── Section totals ──
  const opdTotal     = opd.recd_amt     || 0;
  const opdBillTotal = opdBill.recd_amt || 0;
  const refundTotal  = refund.recd_amt  || 0;
  const ipdTotal     = ipd.recd_amt     || 0;
  const labTotal     = lab.recd_amt     || 0;

  // ── Day-end closing totals (billing + receipt vouchers) ──
  const dayEndCash  = billingCash + receiptCash;
  const dayEndBank  = billingBank + receiptBank;
  const dayEndBoth  = billingBoth + receiptBoth;

  // ── Grand total: billing + receipt vouchers − refunds (credit excluded) ──
  const grandTotal  = billingCash + billingBank + billingBoth + receiptTotal - refundTotal;

  const printTime = nowStr();

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1000, backgroundColor: 'white' }}>
      <PDFViewer width="100%" height="100%" showToolbar>
        <Document title="Bill Wise Collection Summary Report">
          <PDFPage style={styles.page} size="A4" orientation="landscape" wrap>

            {/* ══ HEADER ══════════════════════════════════════════════ */}
            <PDFImage src="/logo_Hospital_m.jpeg" style={styles.logo} />
            <PDFView style={styles.headerRule} />
            <PDFText style={styles.reportTitle}>BILL WISE COLLECTION SUMMARY REPORT</PDFText>
            <PDFText style={styles.reportSub}>Printed on: {printTime}</PDFText>
            <PDFView style={[styles.headerRule, { height: 0.5, marginBottom: 6 }]} />

            {/* ══ TABLE HEADER ════════════════════════════════════════ */}
            <TableHeader />

            {/* ══════════════════════════════════════════════════════════
                SECTION 1 — OPD REGISTRATION
            ════════════════════════════════════════════════════════════ */}
            <SectionHeader title="OPD Registration (Consultancy)" count={data?.opdData?.data?.length} />
            {data?.opdData?.data?.length > 0 ? data.opdData.data.map((row, i) => (
              <PDFView key={i} style={[styles.row, i % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                <TD w={COL.sr} align="center">{i + 1}</TD>
                <TD w={COL.date} align="left">{`${fmtDate(row.date)}\n${rowTime(row)}`}</TD>
                <TD w={COL.type} align="left">OPD</TD>
                <TD w={COL.patient} align="left">{`${row.name_suffix || ''} ${row.first_name || ''} ${row.last_name || ''}`.trim()}</TD>
                <TD w={COL.uhid} align="center">{row.uhid || '-'}</TD>
                <TD w={COL.billNo} align="center">{row.adm_no_date || '-'}</TD>
                <TD w={COL.payMode} align="center">{row.payment_mode || '-'}</TD>
                <TD w={COL.consultant} align="left">{row.consultant?.name || '-'}</TD>
                <TD w={COL.services} align="left">Consultancy</TD>
                <TD w={COL.panel} align="left">{row.sponsor?.name || '-'}</TD>
                <TD w={COL.credit} align="right" color={row.payment_mode?.toUpperCase() === 'CREDIT' ? C.orange : C.dark}>
                  {row.payment_mode?.toUpperCase() === 'CREDIT' ? fmt(row.bal) : '-'}
                </TD>
                <TD w={COL.recd1} align="right">-</TD>
                <TD w={COL.recd2} align="right">-</TD>
                <TD w={COL.total} align="right" bold>{fmt(row.recd)}</TD>
              </PDFView>
            )) : <EmptySection />}
            <SubtotalRow label="OPD Registration Total" amount={opdTotal} />

            {/* ══════════════════════════════════════════════════════════
                SECTION 2 — OPD BILLING
            ════════════════════════════════════════════════════════════ */}
            <SectionHeader title="OPD Billing" count={data?.opdBillingData?.data?.length} />
            {data?.opdBillingData?.data?.length > 0 ? data.opdBillingData.data.map((row, i) => (
              <PDFView key={i} style={[styles.row, i % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                <TD w={COL.sr} align="center">{i + 1}</TD>
                <TD w={COL.date} align="left">{`${fmtDate(row.date)}\n${rowTime(row)}`}</TD>
                <TD w={COL.type} align="left">OPD Bill</TD>
                <TD w={COL.patient} align="left">{`${row.patient?.name_suffix || ''} ${row.patient?.first_name || ''} ${row.patient?.last_name || ''}`.trim()}</TD>
                <TD w={COL.uhid} align="center">{row.uhid || '-'}</TD>
                <TD w={COL.billNo} align="center">{row.patient?.adm_no_date || '-'}</TD>
                <TD w={COL.payMode} align="center">{row.pay_mode || '-'}</TD>
                <TD w={COL.consultant} align="left">{row.patient?.consultant?.name || '-'}</TD>
                <TD w={COL.services} align="left">
                  {(row.combinedServiceDetails || []).map(s => s.service_name || s.test_name).filter(Boolean).join(', ') || '-'}
                </TD>
                <TD w={COL.panel} align="left">{row.patient?.sponsor?.name || '-'}</TD>
                <TD w={COL.credit} align="right" color={row.pay_mode?.toUpperCase() === 'CREDIT' ? C.orange : C.dark}>
                  {row.pay_mode?.toUpperCase() === 'CREDIT' ? fmt(row.balance_amt) : '-'}
                </TD>
                <TD w={COL.recd1} align="right">
                  {row.pay_mode_1 ? `${row.pay_mode_1} ${fmt(row.rec_amt_1)}` : (row.rec_amt_1 ? fmt(row.rec_amt_1) : '-')}
                </TD>
                <TD w={COL.recd2} align="right">
                  {row.pay_mode_2 ? `${row.pay_mode_2} ${fmt(row.rec_amt_2)}` : (row.rec_amt_2 ? fmt(row.rec_amt_2) : '-')}
                </TD>
                <TD w={COL.total} align="right" bold>{fmt(row.total_recd)}</TD>
              </PDFView>
            )) : <EmptySection />}
            <SubtotalRow label="OPD Billing Total" amount={opdBillTotal} />

            {/* ══════════════════════════════════════════════════════════
                SECTION 3 — IPD BILLING
            ════════════════════════════════════════════════════════════ */}
            <SectionHeader title="IPD Billing" count={data?.indoorServiceData?.data?.length} />
            {data?.indoorServiceData?.data?.length > 0 ? data.indoorServiceData.data.map((row, i) => (
              <PDFView key={i} style={[styles.row, i % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                <TD w={COL.sr} align="center">{i + 1}</TD>
                <TD w={COL.date} align="left">{`${fmtDate(row.date)}\n${rowTime(row)}`}</TD>
                <TD w={COL.type} align="left">IPD Bill</TD>
                <TD w={COL.patient} align="left">{`${row.patient?.name_suffix || ''} ${row.patient?.first_name || ''} ${row.patient?.last_name || ''}`.trim()}</TD>
                <TD w={COL.uhid} align="center">{row.patient?.uhid || '-'}</TD>
                <TD w={COL.billNo} align="center">{row.ipd_no || '-'}</TD>
                <TD w={COL.payMode} align="center">{row.pay_mode || '-'}</TD>
                <TD w={COL.consultant} align="left">{row.patient?.consultant?.name || '-'}</TD>
                <TD w={COL.services} align="left">
                  {(row.combinedServiceDetails || []).map(s => s.service_name || s.test_name).filter(Boolean).join(', ') || '-'}
                </TD>
                <TD w={COL.panel} align="left">{row.patient?.sponsor?.name || '-'}</TD>
                <TD w={COL.credit} align="right" color={row.pay_mode?.toUpperCase() === 'CREDIT' ? C.orange : C.dark}>
                  {row.pay_mode?.toUpperCase() === 'CREDIT' ? fmt(row.balance_amt) : '-'}
                </TD>
                <TD w={COL.recd1} align="right">
                  {row.pay_mode_1 ? `${row.pay_mode_1} ${fmt(row.rec_amt_1)}` : (row.rec_amt_1 ? fmt(row.rec_amt_1) : '-')}
                </TD>
                <TD w={COL.recd2} align="right">
                  {row.pay_mode_2 ? `${row.pay_mode_2} ${fmt(row.rec_amt_2)}` : (row.rec_amt_2 ? fmt(row.rec_amt_2) : '-')}
                </TD>
                <TD w={COL.total} align="right" bold>{fmt(row.total_recd)}</TD>
              </PDFView>
            )) : <EmptySection />}
            <SubtotalRow label="IPD Billing Total" amount={ipdTotal} />

            {/* ══════════════════════════════════════════════════════════
                SECTION 4 — LAB INVESTIGATION
            ════════════════════════════════════════════════════════════ */}
            <SectionHeader title="Lab Investigation Billing" count={data?.labInvestigationData?.data?.length} />
            {data?.labInvestigationData?.data?.length > 0 ? data.labInvestigationData.data.map((row, i) => (
              <PDFView key={i} style={[styles.row, i % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                <TD w={COL.sr} align="center">{i + 1}</TD>
                <TD w={COL.date} align="left">{`${fmtDate(row.date)}\n${rowTime(row)}`}</TD>
                <TD w={COL.type} align="left">Lab</TD>
                <TD w={COL.patient} align="left">{row.patient_name || '-'}</TD>
                <TD w={COL.uhid} align="center">{row.uhid || '-'}</TD>
                <TD w={COL.billNo} align="center">{row.ipd_no || row.bill_no || '-'}</TD>
                <TD w={COL.payMode} align="center">{row.pay_mode || '-'}</TD>
                <TD w={COL.consultant} align="left">{row.doctor?.name || '-'}</TD>
                <TD w={COL.services} align="left">
                  {(row.combinedServiceDetails || []).map(s => s.test_name || s.service_name).filter(Boolean).join(', ') || '-'}
                </TD>
                <TD w={COL.panel} align="left">{row.sponsor?.name || '-'}</TD>
                <TD w={COL.credit} align="right" color={row.pay_mode?.toUpperCase() === 'CREDIT' ? C.orange : C.dark}>
                  {row.pay_mode?.toUpperCase() === 'CREDIT' ? fmt(row.balance_amt) : '-'}
                </TD>
                <TD w={COL.recd1} align="right">
                  {row.rec_amt_1 ? fmt(row.rec_amt_1) : '-'}
                </TD>
                <TD w={COL.recd2} align="right">
                  {row.rec_amt_2 ? fmt(row.rec_amt_2) : '-'}
                </TD>
                <TD w={COL.total} align="right" bold>
                  {fmt((row.rec_amt_1 || 0) + (row.rec_amt_2 || 0))}
                </TD>
              </PDFView>
            )) : <EmptySection />}
            <SubtotalRow label="Lab Investigation Total" amount={labTotal} />

            {/* ══════════════════════════════════════════════════════════
                SECTION 5 — RECEIPT VOUCHER
            ════════════════════════════════════════════════════════════ */}
            <SectionHeader title="Receipt Voucher (IPD Advances)" count={data?.receiptVoucherData?.data?.length} />
            {data?.receiptVoucherData?.data?.length > 0 ? data.receiptVoucherData.data.map((row, i) => (
              <PDFView key={i} style={[styles.row, i % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                <TD w={COL.sr} align="center">{i + 1}</TD>
                <TD w={COL.date} align="left">{`${fmtDate(row.date)}\n${rowTime(row)}`}</TD>
                <TD w={COL.type} align="left">Receipt</TD>
                <TD w={COL.patient} align="left">{`${row.patient?.name_suffix || ''} ${row.patient?.first_name || ''} ${row.patient?.last_name || ''}`.trim()}</TD>
                <TD w={COL.uhid} align="center">{row.patient?.uhid || '-'}</TD>
                <TD w={COL.billNo} align="center">{row.ipd_no || '-'}</TD>
                <TD w={COL.payMode} align="center">{row.payment_mode || '-'}</TD>
                <TD w={COL.consultant} align="left">{row.patient?.consultant?.name || '-'}</TD>
                <TD w={COL.services} align="left">-</TD>
                <TD w={COL.panel} align="left">{row.patient?.sponsor?.name || '-'}</TD>
                <TD w={COL.credit} align="right">-</TD>
                <TD w={COL.recd1} align="right">-</TD>
                <TD w={COL.recd2} align="right">-</TD>
                <TD w={COL.total} align="right" bold>{fmt(row.received_amount)}</TD>
              </PDFView>
            )) : <EmptySection />}
            <SubtotalRow label="Receipt Voucher Total" amount={receiptTotal} />

            {/* ══════════════════════════════════════════════════════════
                SECTION 6 — PAYMENT REFUND
            ════════════════════════════════════════════════════════════ */}
            <SectionHeader title="Payment Refund Voucher" count={data?.paymentRefundData?.data?.length} />
            {data?.paymentRefundData?.data?.length > 0 ? data.paymentRefundData.data.map((row, i) => (
              <PDFView key={i} style={[styles.row, i % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                <TD w={COL.sr} align="center">{i + 1}</TD>
                <TD w={COL.date} align="left">{`${fmtDate(row.date)}\n${rowTime(row)}`}</TD>
                <TD w={COL.type} align="left">Refund</TD>
                <TD w={COL.patient} align="left">{`${row.patient?.name_suffix || ''} ${row.patient?.first_name || ''} ${row.patient?.last_name || ''}`.trim()}</TD>
                <TD w={COL.uhid} align="center">{row.patient?.uhid || '-'}</TD>
                <TD w={COL.billNo} align="center">{row.ipd_no || '-'}</TD>
                <TD w={COL.payMode} align="center">{row.payment_mode || '-'}</TD>
                <TD w={COL.consultant} align="left">{row.patient?.consultant?.name || '-'}</TD>
                <TD w={COL.services} align="left">-</TD>
                <TD w={COL.panel} align="left">{row.patient?.sponsor?.name || '-'}</TD>
                <TD w={COL.credit} align="right">-</TD>
                <TD w={COL.recd1} align="right">-</TD>
                <TD w={COL.recd2} align="right">-</TD>
                <TD w={COL.total} align="right" bold color={C.red}>{fmt(row.amount)}</TD>
              </PDFView>
            )) : <EmptySection />}
            <SubtotalRow label="Total Refunded" amount={refundTotal} isRefund />

            {/* ══════════════════════════════════════════════════════════
                SUMMARY SECTION
            ════════════════════════════════════════════════════════════ */}
            <PDFView style={[styles.headerRule, { marginTop: 16, marginBottom: 0, height: 1.5 }]} />
            <PDFText style={{ fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, marginTop: 6, marginBottom: 4 }}>
              COLLECTION SUMMARY
            </PDFText>

            <PDFView style={styles.summaryWrap}>

              {/* Box 1 — Payment Mode Breakdown */}
              <PDFView style={styles.summaryBox}>
                <PDFText style={styles.summaryBoxTitle}>PAYMENT MODE BREAKDOWN</PDFText>

                {/* Billing cash */}
                <PDFView style={[styles.modeRow, { backgroundColor: '#E8F5E9' }]}>
                  <PDFText style={[styles.modeLabel, { fontFamily: 'Roboto', fontWeight: 700, color: C.green }]}>Cash (Billing)</PDFText>
                  <PDFText style={styles.modeAmtGreen}>{fmtINR(billingCash)}</PDFText>
                </PDFView>
                {/* Billing bank */}
                <PDFView style={[styles.modeRow, { backgroundColor: '#E3F2FD' }]}>
                  <PDFText style={[styles.modeLabel, { fontFamily: 'Roboto', fontWeight: 700, color: C.primary }]}>
                    Bank/Digital (Billing)
                  </PDFText>
                  <PDFText style={styles.modeAmt}>{fmtINR(billingBank)}</PDFText>
                </PDFView>
                <PDFView style={styles.modeRow}>
                  <PDFText style={[styles.modeLabel, { color: C.gray }]}>  Bank / UPI / Paytm / NEFT / Card</PDFText>
                  <PDFText style={[styles.modeAmt, { color: C.gray }]}>included above</PDFText>
                </PDFView>
                {billingBoth > 0 && (
                  <PDFView style={[styles.modeRow, { backgroundColor: '#FFF9C4' }]}>
                    <PDFText style={[styles.modeLabel, { color: '#F57F17' }]}>Both Cash+Bank (Billing)</PDFText>
                    <PDFText style={[styles.modeAmt, { color: '#F57F17' }]}>{fmtINR(billingBoth)}</PDFText>
                  </PDFView>
                )}

                {/* Receipt Voucher breakdown */}
                <PDFView style={[styles.modeRow, { backgroundColor: '#EDE7F6', marginTop: 3 }]}>
                  <PDFText style={[styles.modeLabel, { fontFamily: 'Roboto', fontWeight: 700, color: '#4527A0' }]}>
                    Receipt Vouchers (IPD Advances)
                  </PDFText>
                  <PDFText style={[styles.modeAmt, { color: '#4527A0' }]}>{fmtINR(receiptTotal)}</PDFText>
                </PDFView>
                <PDFView style={styles.modeRow}>
                  <PDFText style={[styles.modeLabel, { color: C.gray }]}>  ↳ Cash</PDFText>
                  <PDFText style={[styles.modeAmt, { color: C.green }]}>{fmtINR(receiptCash)}</PDFText>
                </PDFView>
                <PDFView style={styles.modeRow}>
                  <PDFText style={[styles.modeLabel, { color: C.gray }]}>  ↳ Bank / Digital</PDFText>
                  <PDFText style={styles.modeAmt}>{fmtINR(receiptBank)}</PDFText>
                </PDFView>
                {receiptBoth > 0 && (
                  <PDFView style={styles.modeRow}>
                    <PDFText style={[styles.modeLabel, { color: C.gray }]}>  ↳ Both</PDFText>
                    <PDFText style={[styles.modeAmt, { color: '#F57F17' }]}>{fmtINR(receiptBoth)}</PDFText>
                  </PDFView>
                )}

                <PDFView style={[styles.modeRow, { backgroundColor: '#FFEBEE', marginTop: 3 }]}>
                  <PDFText style={[styles.modeLabel, { color: C.red }]}>(−) Refunds</PDFText>
                  <PDFText style={styles.modeAmtRed}>({fmtINR(refundTotal)})</PDFText>
                </PDFView>
                <PDFView style={[styles.modeRowLast, { backgroundColor: '#1B5E20', borderTopWidth: 1, borderTopColor: C.primary }]}>
                  <PDFText style={[styles.modeLabel, { fontFamily: 'Roboto', fontWeight: 700, color: C.white }]}>NET COLLECTED</PDFText>
                  <PDFText style={[styles.modeAmt, { color: '#A5D6A7' }]}>{fmtINR(grandTotal)}</PDFText>
                </PDFView>
              </PDFView>

              {/* Box 2 — Section-wise Breakdown */}
              <PDFView style={styles.sectionBox}>
                <PDFText style={styles.sBoxTitle}>COLLECTION BY SECTION</PDFText>
                <PDFView style={styles.sBoxRow}>
                  <PDFText style={styles.sBoxLabel}>OPD Registration</PDFText>
                  <PDFText style={styles.sBoxAmt}>{fmtINR(opdTotal)}</PDFText>
                </PDFView>
                <PDFView style={styles.sBoxRow}>
                  <PDFText style={styles.sBoxLabel}>OPD Billing</PDFText>
                  <PDFText style={styles.sBoxAmt}>{fmtINR(opdBillTotal)}</PDFText>
                </PDFView>
                <PDFView style={styles.sBoxRow}>
                  <PDFText style={styles.sBoxLabel}>IPD Billing</PDFText>
                  <PDFText style={styles.sBoxAmt}>{fmtINR(ipdTotal)}</PDFText>
                </PDFView>
                <PDFView style={styles.sBoxRow}>
                  <PDFText style={styles.sBoxLabel}>Lab Investigation</PDFText>
                  <PDFText style={styles.sBoxAmt}>{fmtINR(labTotal)}</PDFText>
                </PDFView>
                <PDFView style={[styles.sBoxRow, { backgroundColor: '#EDE7F6' }]}>
                  <PDFText style={[styles.sBoxLabel, { fontFamily: 'Roboto', fontWeight: 700, color: '#4527A0' }]}>Receipt Vouchers</PDFText>
                  <PDFText style={[styles.sBoxAmt, { color: '#4527A0' }]}>{fmtINR(receiptTotal)}</PDFText>
                </PDFView>
                <PDFView style={[styles.sBoxRow, { backgroundColor: '#FFEBEE' }]}>
                  <PDFText style={[styles.sBoxLabel, { color: C.red }]}>(−) Refunds</PDFText>
                  <PDFText style={styles.sBoxAmtRed}>({fmtINR(refundTotal)})</PDFText>
                </PDFView>
                <PDFView style={[styles.sBoxRowLast, { backgroundColor: '#E8F5E9', borderTopWidth: 1, borderTopColor: C.primary }]}>
                  <PDFText style={[styles.sBoxLabel, { fontFamily: 'Roboto', fontWeight: 700, color: C.green }]}>Total Net Collected</PDFText>
                  <PDFText style={[styles.sBoxAmt, { color: C.green }]}>{fmtINR(grandTotal)}</PDFText>
                </PDFView>
              </PDFView>

            </PDFView>

            {/* Grand Total Banner */}
            <PDFView style={styles.grandBox}>
              <PDFView style={styles.grandRow}>
                <PDFText style={styles.grandLabel}>
                  GRAND TOTAL COLLECTED  (Billing + Receipt Vouchers − Refunds)
                </PDFText>
                <PDFText style={styles.grandAmt}>{fmtINR(grandTotal)}</PDFText>
              </PDFView>
            </PDFView>

            {/* ══ DAY-END CLOSING SUMMARY ══════════════════════════════ */}
            <PDFView style={{ marginTop: 12, borderWidth: 1.5, borderColor: C.primaryDark, borderRadius: 4 }}>
              {/* Header row */}
              <PDFView style={{ backgroundColor: C.primaryDark, flexDirection: 'row', paddingVertical: 4 }}>
                <PDFText style={{ flex: 2, fontSize: 7.5, fontFamily: 'Roboto', fontWeight: 700, color: C.white, paddingHorizontal: 8 }}>
                  DAY-END CLOSING SUMMARY
                </PDFText>
                <PDFText style={{ width: '20%', fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: '#A5D6A7', textAlign: 'right', paddingHorizontal: 6 }}>CASH</PDFText>
                <PDFText style={{ width: '20%', fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: '#90CAF9', textAlign: 'right', paddingHorizontal: 6 }}>BANK / DIGITAL</PDFText>
                <PDFText style={{ width: '20%', fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.white, textAlign: 'right', paddingHorizontal: 6 }}>TOTAL</PDFText>
              </PDFView>

              {/* Billing row */}
              <PDFView style={{ flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: C.border, paddingVertical: 4, backgroundColor: C.white }}>
                <PDFText style={{ flex: 2, fontSize: 7, color: C.dark, paddingHorizontal: 8 }}>Billing (OPD + OPD Bill + IPD + Lab)</PDFText>
                <PDFText style={{ width: '20%', fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.green, textAlign: 'right', paddingHorizontal: 6 }}>{fmtINR(billingCash)}</PDFText>
                <PDFText style={{ width: '20%', fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primary, textAlign: 'right', paddingHorizontal: 6 }}>{fmtINR(billingBank)}</PDFText>
                <PDFText style={{ width: '20%', fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.dark, textAlign: 'right', paddingHorizontal: 6 }}>{fmtINR(billingCash + billingBank + billingBoth)}</PDFText>
              </PDFView>

              {/* Receipt Voucher row */}
              <PDFView style={{ flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: C.border, paddingVertical: 4, backgroundColor: '#EDE7F6' }}>
                <PDFText style={{ flex: 2, fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: '#4527A0', paddingHorizontal: 8 }}>Receipt Vouchers (IPD Advances)</PDFText>
                <PDFText style={{ width: '20%', fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.green, textAlign: 'right', paddingHorizontal: 6 }}>{fmtINR(receiptCash)}</PDFText>
                <PDFText style={{ width: '20%', fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primary, textAlign: 'right', paddingHorizontal: 6 }}>{fmtINR(receiptBank)}</PDFText>
                <PDFText style={{ width: '20%', fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: '#4527A0', textAlign: 'right', paddingHorizontal: 6 }}>{fmtINR(receiptTotal)}</PDFText>
              </PDFView>

              {/* Refund row */}
              {refundTotal > 0 && (
                <PDFView style={{ flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: C.border, paddingVertical: 4, backgroundColor: '#FFEBEE' }}>
                  <PDFText style={{ flex: 2, fontSize: 7, color: C.red, paddingHorizontal: 8 }}>(−) Payment Refunds</PDFText>
                  <PDFText style={{ width: '20%', fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.red, textAlign: 'right', paddingHorizontal: 6 }}>-</PDFText>
                  <PDFText style={{ width: '20%', fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.red, textAlign: 'right', paddingHorizontal: 6 }}>-</PDFText>
                  <PDFText style={{ width: '20%', fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.red, textAlign: 'right', paddingHorizontal: 6 }}>({fmtINR(refundTotal)})</PDFText>
                </PDFView>
              )}

              {/* Net closing row */}
              <PDFView style={{ flexDirection: 'row', paddingVertical: 6, backgroundColor: C.primaryDark }}>
                <PDFText style={{ flex: 2, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.white, paddingHorizontal: 8 }}>NET CLOSING BALANCE</PDFText>
                <PDFText style={{ width: '20%', fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, color: '#A5D6A7', textAlign: 'right', paddingHorizontal: 6 }}>{fmtINR(dayEndCash)}</PDFText>
                <PDFText style={{ width: '20%', fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, color: '#90CAF9', textAlign: 'right', paddingHorizontal: 6 }}>{fmtINR(dayEndBank)}</PDFText>
                <PDFText style={{ width: '20%', fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, color: '#FFD54F', textAlign: 'right', paddingHorizontal: 6 }}>{fmtINR(grandTotal)}</PDFText>
              </PDFView>
            </PDFView>

            {/* Credit Outstanding Notice */}
            {totalCredit > 0 && (
              <PDFView style={styles.creditBox}>
                <PDFText style={styles.creditLabel}>
                  * Credit Outstanding — on account, NOT collected (Cash/Bank flow excluded):
                </PDFText>
                <PDFText style={styles.creditAmt}>{fmtINR(totalCredit)}</PDFText>
              </PDFView>
            )}

            {/* ══ PAGE FOOTER ══════════════════════════════════════════ */}
            <PDFView style={styles.footer} fixed>
              <PDFText style={styles.footerLeft}>Bill Wise Collection Summary  |  Printed: {printTime}</PDFText>
              <PDFText style={styles.footerRight} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
            </PDFView>

          </PDFPage>
        </Document>
      </PDFViewer>
    </div>
  );
};

export default CollectionSummaryReport;
